const normalizeMediaPath = (pathname) => {
  const path = `/${String(pathname || '').replace(/^\/+/, '')}`;
  const withoutDuplicatePrefix = path.replace(/^\/media\/storage\/media(?:\/|$)/, '/media/');
  if (withoutDuplicatePrefix === '/storage/media') return '/media/';
  if (withoutDuplicatePrefix.startsWith('/storage/media/')) {
    return `/media/${withoutDuplicatePrefix.slice('/storage/media/'.length)}`;
  }
  if (withoutDuplicatePrefix === '/media') return '/media/';
  if (withoutDuplicatePrefix.startsWith('/media/')) return withoutDuplicatePrefix;
  return `/media/${withoutDuplicatePrefix.replace(/^\/+/, '').replace(/^media\//, '')}`;
};

const isKnownMediaPath = (pathname) => /^\/(?:media|storage\/media)(?:\/|$)/.test(pathname);

const hasSigningParameters = (url) =>
  Array.from(url.searchParams.keys()).some((key) =>
    /(?:^|[-_])(signature|sig|token|credential|expires|policy|key-pair-id)(?:$|[-_])/i.test(key)
  );

const MEDIA_URL_BASE = 'https://media.invalid';

const currentOrigin = () => {
  try {
    const origin = globalThis?.location?.origin;
    return origin && origin !== 'null' ? new URL(origin).origin : null;
  } catch {
    return null;
  }
};

export const normalizeShadingMediaUrl = (value, sameOrigin = currentOrigin()) => {
  const normalized = String(value || '').trim();
  if (!normalized || normalized.startsWith('//')) return null;

  try {
    const absolute = new URL(normalized);
    if (!['http:', 'https:'].includes(absolute.protocol)) return null;

    let normalizedOrigin = null;
    try {
      normalizedOrigin = sameOrigin ? new URL(sameOrigin).origin : null;
    } catch {
      normalizedOrigin = null;
    }

    if (absolute.origin !== normalizedOrigin || !isKnownMediaPath(absolute.pathname) || hasSigningParameters(absolute)) {
      return normalized;
    }

    absolute.pathname = normalizeMediaPath(absolute.pathname);
    return absolute.toString();
  } catch {
    // A colon before the first slash denotes a non-HTTP URI scheme rather
    // than a relative media path. Do not turn it into a fetchable local URL.
    const schemeSeparator = normalized.indexOf(':');
    const firstSlash = normalized.indexOf('/');
    if (schemeSeparator >= 0 && (firstSlash < 0 || schemeSeparator < firstSlash)) {
      return null;
    }

    try {
      const relativeUrl = new URL(normalized, MEDIA_URL_BASE);
      if (hasSigningParameters(relativeUrl)) return normalized;
    } catch {}

    const queryIndex = normalized.search(/[?#]/);
    const pathname = queryIndex >= 0 ? normalized.slice(0, queryIndex) : normalized;
    const suffix = queryIndex >= 0 ? normalized.slice(queryIndex) : '';
    return `${normalizeMediaPath(pathname)}${suffix}`;
  }
};

export const addShadingMediaCacheBuster = (value, version = Date.now()) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;

  try {
    const isAbsolute = /^https?:\/\//i.test(normalized);
    const url = new URL(normalized, MEDIA_URL_BASE);
    if (hasSigningParameters(url)) return normalized;

    url.searchParams.set('v', String(version));
    if (isAbsolute) return url.toString();
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return normalized;
  }
};

export default normalizeShadingMediaUrl;
